import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Phone, Search, ChevronDown, Smartphone } from "lucide-react";
import { CONTACT, PROGRAMS_BY_DELIVERY } from "../../data/homeData";
import { INTERNAL_PROGRAM_SLUGS } from "../../data/programSlugs";
import { useRequestInfo } from "../forms/RequestInfoModal";
import {
  reopenPwaInstallTile,
  shouldShowInstallAppNavLink,
} from "../notificationQueueBridge";
import Logo from "../Logo";

const programLink = (label) => {
  const slug = INTERNAL_PROGRAM_SLUGS[label];
  if (slug) return { to: `/programs/${slug}`, external: false };
  // Unknown program label → route to the programs index with a query
  // hint (rather than depending on staging21, which is going offline).
  return {
    to: `/programs?q=${encodeURIComponent(label)}`,
    external: false,
  };
};

const ADMISSIONS_LINKS = [
  { label: "Admissions Home", to: "/admissions", external: false },
  { label: "How to Apply", to: "/admissions/how-to-apply", external: false },
  { label: "International Students", to: "/admissions/international-students", external: false },
  { label: "Refer a Friend", to: "/refer-a-friend", external: false },
];

// Financial Aid — order + labels mirror staging21 dropdown 1:1.
// "OSAP" has no standalone page on either site; on staging it lives as a
// section on the Financial Aid landing page, so we route it there.
const FINANCIAL_AID_LINKS = [
  { label: "Financial Aid", to: "/financial-aid" },
  { label: "Better Jobs Ontario", to: "/financial-aid/better-jobs-ontario" },
  { label: "Alumni Bursary", to: "/financial-aid/alumni-bursary" },
  { label: "Cardiology Technologist Bursary", to: "/financial-aid/cardiology-bursary" },
  { label: "Windmill Microlending", to: "/financial-aid/windmill-microlending" },
  { label: "New to Canada", to: "/financial-aid/new-to-canada" },
  { label: "Student Lines of Credit", to: "/financial-aid/student-lines-of-credit" },
  { label: "OSAP", to: "/financial-aid#osap" },
  { label: "Out-of-province Students", to: "/financial-aid/out-of-province" },
];

const WHY_LINKS = [
  { label: "Why Anderson?", to: "/why-anderson" },
  { label: "140 Years of Making Dreams Come True", to: "/why-anderson/140-years" },
  { label: "Land Acknowledgements", to: "/why-anderson/land-acknowledgements" },
  { label: "Press Corner", to: "/why-anderson/press-corner" },
  { label: "Faculty", to: "/why-anderson/faculty" },
  { label: "Career Services", to: "/career-services" },
  { label: "Testimonials", to: "/why-anderson/testimonials" },
  { label: "Work at Anderson", to: "/careers" },
];

// Student Life — sub-menu mirrors the staging dropdown structure.
// External links open the live anderson production / staging page;
// internal pages render natively (Need Help Jotform embed, GMSSP).
const PROD = "https://www.andersoncollege.com";
const STUDENT_LIFE_LINKS = [
  { label: "Student Life Home", to: "/student-life", external: false },
  { label: "Student Portal", to: `${PROD}/student-portal/`, external: true },
  { label: "Need Help?", to: "/student-life/need-help", external: false },
  { label: "Complimentary OPSWA Membership", to: "/student-life/opswa-membership", external: false },
  { label: "Complimentary ODAA Membership", to: "/student-life/odaa-membership", external: false },
  { label: "GuardMe Student Support (GMSSP)", to: "/student-life/gmssp", external: false },
  { label: "Digital Credentials FAQ", to: "/student-life/digital-credentials-faq", external: false },
  { label: "Resources", to: "/student-life/resources", external: false },
  { label: "Bookstore", to: "/student-life/bookstore", external: false },
  { label: "Share Your Story", to: "/student-life/share-your-story", external: false },
  { label: "Blog", to: "/blog", external: false },
];

const MENU = [
  { key: "online", label: "Online Programs", dropdown: "online" },
  { key: "on-campus", label: "On-Campus Programs", dropdown: "on-campus" },
  { key: "admissions", label: "Admissions", dropdown: "admissions" },
  { key: "why", label: "Why Anderson", dropdown: "why" },
  { key: "life", label: "Student Life", dropdown: "life" },
  { key: "aid", label: "Financial Aid", dropdown: "financial-aid" },
];

const MegaDropdownPanel = ({ groups }) => (
  // Wide mega panels (Online Programs, On-Campus Programs) used to anchor at
  // the trigger button's left edge with `absolute left-0`. That overflowed the
  // viewport on the right side because the buttons sit left-of-centre but the
  // panel is 820 px wide. Switching to `fixed` + `left:50%` + `translateX(-50%)`
  // keeps the panel centred to the viewport regardless of which button was
  // hovered, and the `min(900px, calc(100vw - 2rem))` width clamp guarantees
  // it never reaches either edge. Hover continuity with the button is
  // preserved by the parent `onMouseLeave` (React event tree, not DOM).
  // 2026-05-04: staggered fade+slide-up entrance animation per category column
  // (0.1s delay each, ease-out) + 10% bump on category-grid typography.
  <div
    className="fixed mt-0 pt-3 z-[1100] left-1/2 -translate-x-1/2"
    style={{
      width: "min(900px, calc(100vw - 2rem))",
      // Anchor directly beneath the actual header bottom edge (measured
      // in Header.jsx and published as --ac-header-bottom). Fallback of
      // 76px preserves parity with the previous hard-coded value if the
      // variable hasn't been set yet on first paint.
      top: "var(--ac-header-bottom, 76px)",
    }}
    data-testid="mega-dropdown"
  >
    <style>{`
      @keyframes ac-mega-stagger {
        from { opacity: 0; transform: translateY(12px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .ac-mega-col {
        opacity: 0;
        animation: ac-mega-stagger 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
      }
      @media (prefers-reduced-motion: reduce) {
        .ac-mega-col { animation: none; opacity: 1; }
      }
    `}</style>
    <div className="bg-white shadow-[0_30px_60px_-20px_rgba(0,0,0,0.25)] border border-[var(--ac-line)] rounded-lg overflow-hidden">
      <div className="grid md:grid-cols-3 gap-0">
        {groups.map((g, i) => (
          <div
            key={g.group}
            className="ac-mega-col p-5 border-r border-b md:border-b-0 border-[var(--ac-line)] last:border-r-0 min-w-0"
            style={{ animationDelay: `${i * 0.1}s` }}
          >
            <div className="text-[12px] font-bold uppercase tracking-[0.14em] text-[var(--ac-royal)] mb-3 break-words">
              {g.group}
            </div>
            <ul className="space-y-1.5">
              {g.items.map((item) => {
                const link = programLink(item);
                const cls = "block py-1.5 text-[15px] text-[var(--ac-ink)] hover:text-[var(--ac-red)] leading-snug transition break-words";
                return (
                  <li key={item} data-testid={`mega-item-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {link.external ? (
                      <a href={link.to} target="_blank" rel="noopener noreferrer" className={cls}>
                        {item}
                      </a>
                    ) : (
                      <Link to={link.to} className={cls}>
                        {item}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-[var(--ac-line)] bg-[#faf8f4] px-5 py-3">
        <Link
          to="/programs"
          data-testid="mega-view-all-programs"
          className="inline-flex items-center gap-2 text-[14.5px] font-semibold text-[var(--ac-red)] hover:gap-3 transition-all"
          style={{ fontFamily: "var(--font-display)" }}
        >
          View All Programs <span aria-hidden="true">→</span>
        </Link>
      </div>
    </div>
  </div>
);

import TextListPanel from "../header/TextListPanel";

const AdmissionsPanel = () => {
  // 2026-06-02 product brief: removed the conditional "Download App"
  // footer per user request. PWA install is still discoverable via the
  // home-page tile + the install bubble that surfaces on first visit.
  return (
    <TextListPanel
      testIdPrefix="admissions"
      items={ADMISSIONS_LINKS}
      width={280}
    />
  );
};

const WhyPanel = () => (
  <TextListPanel testIdPrefix="why" items={WHY_LINKS} width={320} />
);

const StudentLifePanel = () => (
  <TextListPanel testIdPrefix="life" items={STUDENT_LIFE_LINKS} width={340} />
);

const FinancialAidPanel = () => (
  <TextListPanel testIdPrefix="financial-aid" items={FINANCIAL_AID_LINKS} width={320} />
);

const Header = () => {
  const { open: openRequestInfo } = useRequestInfo();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileSection, setMobileSection] = useState(null);
  const timer = useRef(null);
  const headerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Publish the header's actual bottom edge as a CSS variable so the
  // wide mega-dropdown (Online / On-Campus Programs) sits flush beneath
  // the nav on every viewport & scroll state, matching the Admissions
  // dropdown behaviour. Fixes bug where the 900px panel visually
  // overlapped the header logo/nav strip on desktop (2026-08-25).
  useEffect(() => {
    const setVar = () => {
      const h = headerRef.current;
      if (!h) return;
      const bottom = Math.round(h.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty("--ac-header-bottom", `${bottom}px`);
    };
    setVar();
    window.addEventListener("resize", setVar);
    // Re-measure after the scrolled-state transition finishes so the
    // dropdown re-anchors when the top strip collapses.
    const t = setTimeout(setVar, 450);
    return () => {
      window.removeEventListener("resize", setVar);
      clearTimeout(t);
    };
  }, [scrolled]);

  const openWith = (key) => {
    if (timer.current) clearTimeout(timer.current);
    setOpenMenu(key);
  };
  const closeSoon = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpenMenu(null), 150);
  };

  const handleMenuClick = (item) => {
    if (item.dropdown) {
      setOpenMenu(openMenu === item.key ? null : item.key);
      return;
    }
    if (item.external) {
      window.open(item.href, "_blank", "noopener,noreferrer");
    } else if (item.href.startsWith("/#")) {
      // hash link — navigate home and scroll
      navigate("/");
      setTimeout(() => {
        const id = item.href.slice(2);
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 50);
    } else {
      navigate(item.href);
    }
    setOpenMenu(null);
  };

  return (
    <header
      ref={headerRef}
      data-testid="site-header"
      className={`fixed left-0 right-0 z-[1000] transition-[background-color,box-shadow] duration-[400ms] ease-in-out transform-gpu ${
        scrolled
          ? "bg-white shadow-[0_4px_22px_-16px_rgba(0,0,0,0.25)]"
          : "bg-transparent"
      }`}
      data-scrolled={scrolled ? "true" : "false"}
      style={{
        // Force a GPU-compositor layer so iOS Safari + Android Chrome
        // don't reposition the fixed header when the URL bar collapses
        // during scroll. Without this, the logo visibly "jumps" on
        // address-bar show/hide. (Fix for 2026-06 mobile feedback.)
        transform: "translate3d(0,0,0)",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
        WebkitTransform: "translate3d(0,0,0)",
        // 2026-02 PageSpeed CLS fix: Header now stays anchored at
        // `top: 0`. Previously `top: var(--ac-banner-offset, 0px)`
        // pushed the Header down when the AnnouncementBar mounted
        // from the async CMS fetch, which was a late layout shift
        // of a fixed element — a common field-CLS tail contributor.
        // The AnnouncementBar overlays at z=1001 (above the Header
        // at z=1000) rather than pushing it. On desktop that visually
        // sits over the first ~26px top-strip of the Header; on
        // mobile the Header's compact 64px row stays fully readable
        // because the AnnouncementBar is at the very top edge.
        top: 0,
      }}
    >
      {/* Readability backdrop over hero imagery when transparent (mobile + desktop) */}
      {!scrolled && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/45 via-black/20 to-transparent"
        />
      )}
      <div className="relative">
      {/* Top strip */}
      <div className={`hidden md:block text-white text-[13px] transition-colors duration-300 ${scrolled ? "bg-[var(--ac-royal)]" : "bg-transparent"}`}>
        <div className="ac-container flex justify-between items-center py-1.5">
          <a
            href={CONTACT.phoneHref}
            className="group flex items-center gap-2 text-[14px] font-semibold tracking-wide hover:text-white transition"
            data-testid="top-phone"
          >
            <span className="ac-phone-pulse">
              <Phone className="w-4 h-4 text-white group-hover:text-[var(--ac-red)] transition-colors" fill="currentColor" />
            </span>
            <span>{CONTACT.phone}</span>
          </a>
          <div className="flex items-center gap-3 xl:gap-5 text-[11px] uppercase tracking-[0.14em] font-semibold whitespace-nowrap">
            <a href="/discovery-quiz" className="hover:text-[#f5c056] transition inline-flex items-center gap-1.5" data-testid="top-quiz">
              <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              Career Discovery Quiz
            </a>
            <Link to="/campuses" className="hover:text-[#f5c056] transition" data-testid="top-campuses">Campuses</Link>
            <Link to="/student-life" className="hover:text-[#f5c056] transition" data-testid="top-student-resources">Student Resources</Link>
            <Link to="/student-portal" className="hover:text-[#f5c056] transition" data-testid="top-student-portal">Student Portal</Link>
            <button
              type="button"
              onClick={() => navigate("/search")}
              aria-label="Search"
              className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] hover:text-[#f5c056] transition"
              data-testid="top-search"
            >
              <Search className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Main nav — fixed slim ribbon. Bar height is the source of truth;
          the logo (height: 100% + object-fit: contain) fills it. Menu items
          are vertically centered via flex, no per-item vertical padding. */}
      <div className="ac-container flex items-center justify-between gap-3 md:gap-4 h-[64px] md:h-[76px]">
        <Link
          to="/"
          className="relative z-10 flex items-center h-full pr-8"
          data-testid="logo-link"
          aria-label="Anderson College — Home"
        >
          {/* Logo variant follows header background:
              - Transparent header (over hero imagery) → white transparent logo
              - Scrolled / solid white header → full-colour shield + wordmark
              The new transparent assets (2026-04-29) make the rectangular
              white-block fallback unnecessary. */}
          <Logo
            variant={scrolled ? "dark" : "light"}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-6" data-testid="primary-nav">
          {MENU.map((item) => {
            const isOpen = openMenu === item.key;
            const isDropdown = !!item.dropdown;
            const linkColor = scrolled
              ? "text-[var(--ac-ink)] hover:text-[var(--ac-red)]"
              : "text-white hover:text-[#f5c056]";
            return (
              <div
                key={item.key}
                className="relative"
                onMouseEnter={() => isDropdown && openWith(item.key)}
                onMouseLeave={() => isDropdown && closeSoon()}
              >
                <button
                  type="button"
                  onClick={() => handleMenuClick(item)}
                  className={`flex items-center gap-1 whitespace-nowrap text-[11px] xl:text-[12px] font-bold uppercase tracking-[0.04em] transition ${linkColor}`}
                  data-testid={`nav-${item.key}`}
                  aria-expanded={isDropdown ? isOpen : undefined}
                  aria-haspopup={isDropdown ? "menu" : undefined}
                >
                  {item.accent && (
                    <span
                      aria-hidden="true"
                      className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 animate-pulse"
                      style={{ background: "#F37021" }}
                    />
                  )}
                  {item.label}
                  {isDropdown && <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />}
                </button>
                {isDropdown && isOpen && (
                  <>
                    {item.dropdown === "online" && <MegaDropdownPanel groups={PROGRAMS_BY_DELIVERY.online} />}
                    {item.dropdown === "on-campus" && <MegaDropdownPanel groups={PROGRAMS_BY_DELIVERY["on-campus"]} />}
                    {item.dropdown === "admissions" && <AdmissionsPanel />}
                    {item.dropdown === "why" && <WhyPanel />}
                    {item.dropdown === "life" && <StudentLifePanel />}
                    {item.dropdown === "financial-aid" && <FinancialAidPanel />}
                  </>
                )}
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/admissions#book"
            className="!hidden lg:!inline-flex ds-btn ds-btn--primary text-[11px] md:text-[12px] px-4 md:px-5 py-2 md:py-2.5"
            data-testid="header-book-appointment"
            onClick={(e) => {
              e.preventDefault();
              openRequestInfo();
            }}
          >
            Get Started
          </Link>
          <button
            className={`lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-md transition ${
              scrolled
                ? "text-[var(--ac-ink)] hover:bg-[#f3f4f6]"
                : "text-white hover:bg-white/15"
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-panel"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      </div>

      {/* Mobile nav panel — full-screen overlay (top-to-bottom) */}
      {/* Mobile nav drawer — rendered via createPortal into <body> to escape
          the header's transform-induced containing block. The <header> uses
          `transform: translate3d(0,0,0)` (iOS URL-bar fix, line 247) which
          per CSS spec makes any descendant `position: fixed` element render
          relative to the header's 64 px box instead of the viewport. The
          portal mounts the panel as a direct child of <body>, restoring the
          intended full-viewport overlay behaviour. */}
      {mobileOpen && createPortal(
        <div
          id="mobile-nav-panel"
          className="lg:hidden fixed inset-0 z-[1200] bg-white overflow-y-auto"
          data-testid="mobile-nav-panel"
          role="dialog"
          aria-modal="true"
        >
          {/* Top bar inside the overlay so the close (X) is always reachable
              while the menu is full-screen. `h-16` is REQUIRED — Logo.jsx
              uses h-[48%], so an intrinsic (content-sized) height collapses
              the logo to 0×0 px. The matching `h-full` on the anchor lets
              the Logo's 48 % resolve against the 64 px row. */}
          <div className="sticky top-0 z-10 bg-white border-b border-[var(--ac-line)] px-4 h-16 flex items-center justify-between">
            <Link to="/" onClick={() => setMobileOpen(false)} className="inline-flex items-center h-full" data-testid="mobile-nav-logo" aria-label="Anderson College — Home">
              <Logo variant="dark" />
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
              data-testid="mobile-nav-close"
              className="w-11 h-11 inline-flex items-center justify-center rounded-md text-[var(--ac-ink)] hover:bg-[var(--ac-cream)]"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="ac-container py-5 flex flex-col gap-0">
            {/* High-converting lead magnet — first thing on mobile */}
            <a
              href="/discovery-quiz"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-between py-4 text-[17px] font-bold uppercase tracking-[0.06em] text-[var(--ac-ink)] hover:text-[var(--ac-red)] border-b border-[var(--ac-line)]"
              data-testid="mobile-nav-discovery-quiz"
            >
              <span className="inline-flex items-center gap-2">
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--ac-red)] animate-pulse"
                  aria-hidden="true"
                />
                Career Discovery Quiz
              </span>
              <span className="text-[11px] font-bold tracking-[0.18em] text-[var(--ac-red)]">3 MIN</span>
            </a>
            {MENU.map((item) => {
              const isSec = mobileSection === item.key;
              if (!item.dropdown) {
                const external = item.external;
                return (
                  <a
                    key={item.key}
                    href={item.href}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noopener noreferrer" : undefined}
                    onClick={() => setMobileOpen(false)}
                    className="py-4 text-[17px] font-bold uppercase tracking-[0.06em] text-[var(--ac-ink)] hover:text-[var(--ac-red)] border-b border-[var(--ac-line)]"
                    data-testid={`mobile-nav-${item.key}`}
                  >
                    {item.label}
                  </a>
                );
              }
              return (
                <div key={item.key} className="border-b border-[var(--ac-line)]">
                  <button
                    type="button"
                    onClick={() => setMobileSection(isSec ? null : item.key)}
                    className="w-full flex items-center justify-between py-4 text-[17px] font-bold uppercase tracking-[0.06em] text-[var(--ac-ink)] hover:text-[var(--ac-red)]"
                    data-testid={`mobile-nav-${item.key}`}
                    aria-expanded={isSec}
                  >
                    <span>{item.label}</span>
                    <ChevronDown className={`w-5 h-5 transition-transform ${isSec ? "rotate-180" : ""}`} />
                  </button>
                  {isSec && (
                    <div className="pb-4 pl-3" data-testid={`mobile-nav-${item.key}-panel`}>
                      {item.dropdown === "admissions"
                        ? (
                          <>
                            {ADMISSIONS_LINKS.map((a) => (
                              <Link
                                key={a.to}
                                to={a.to}
                                onClick={() => setMobileOpen(false)}
                                className="block py-2.5 text-[15.5px] text-[var(--ac-ink)] hover:text-[var(--ac-red)]"
                              >
                                {a.label}
                              </Link>
                            ))}
                            {/* 2026-06-02 product brief: removed mobile
                                "Download App" CTA from the Admissions
                                menu per user request. */}
                          </>
                        )
                        : item.dropdown === "why"
                        ? WHY_LINKS.map((a) => (
                            <Link
                              key={a.to}
                              to={a.to}
                              onClick={() => setMobileOpen(false)}
                              className="block py-2.5 text-[15.5px] text-[var(--ac-ink)] hover:text-[var(--ac-red)]"
                            >
                              {a.label}
                            </Link>
                          ))
                        : item.dropdown === "life"
                        ? STUDENT_LIFE_LINKS.map((a) =>
                            a.external ? (
                              <a
                                key={a.to}
                                href={a.to}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setMobileOpen(false)}
                                className="block py-2.5 text-[15.5px] text-[var(--ac-ink)] hover:text-[var(--ac-red)]"
                              >
                                {a.label}
                              </a>
                            ) : (
                              <Link
                                key={a.to}
                                to={a.to}
                                onClick={() => setMobileOpen(false)}
                                className="block py-2.5 text-[15.5px] text-[var(--ac-ink)] hover:text-[var(--ac-red)]"
                              >
                                {a.label}
                              </Link>
                            )
                          )
                        : item.dropdown === "financial-aid"
                        ? FINANCIAL_AID_LINKS.map((a) => (
                            <Link
                              key={`${a.label}-${a.to}`}
                              to={a.to}
                              onClick={() => setMobileOpen(false)}
                              className="block py-2.5 text-[15.5px] text-[var(--ac-ink)] hover:text-[var(--ac-red)]"
                            >
                              {a.label}
                            </Link>
                          ))
                        : (
                          <>
                            {(PROGRAMS_BY_DELIVERY[item.dropdown] || []).map((g) => (
                              <div key={g.group} className="mb-3">
                                <div className="text-[12px] font-bold uppercase tracking-[0.14em] text-[var(--ac-royal)] mb-1.5">{g.group}</div>
                                {g.items.map((it) => {
                                  const link = programLink(it);
                                  return link.external ? (
                                    <a key={it} href={link.to} target="_blank" rel="noopener noreferrer" className="block py-2 text-[15.5px] text-[var(--ac-ink)] hover:text-[var(--ac-red)]" onClick={() => setMobileOpen(false)}>
                                      {it}
                                    </a>
                                  ) : (
                                    <Link key={it} to={link.to} className="block py-2 text-[15.5px] text-[var(--ac-ink)] hover:text-[var(--ac-red)]" onClick={() => setMobileOpen(false)}>
                                      {it}
                                    </Link>
                                  );
                                })}
                              </div>
                            ))}
                            <Link
                              to="/programs"
                              data-testid={`mobile-view-all-programs-${item.key}`}
                              onClick={() => setMobileOpen(false)}
                              className="block mt-2 pt-3 border-t border-[var(--ac-line)] text-[15px] font-semibold text-[var(--ac-red)]"
                            >
                              View All Programs →
                            </Link>
                          </>
                        )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Utility links (Campuses · Student Resources · Search) — mirror
                the desktop top utility strip so mobile users can still reach
                them without the burger swallowing those entry points. */}
            <div className="mt-2 flex flex-col" data-testid="mobile-nav-utility">
              <Link
                to="/campuses"
                onClick={() => setMobileOpen(false)}
                className="py-3.5 text-[15px] font-bold uppercase tracking-[0.06em] text-[var(--ac-ink)] hover:text-[var(--ac-red)] border-b border-[var(--ac-line)]"
                data-testid="mobile-nav-campuses"
              >
                Campuses
              </Link>
              <Link
                to="/student-life"
                onClick={() => setMobileOpen(false)}
                className="py-3.5 text-[15px] font-bold uppercase tracking-[0.06em] text-[var(--ac-ink)] hover:text-[var(--ac-red)] border-b border-[var(--ac-line)]"
                data-testid="mobile-nav-student-resources"
              >
                Student Resources
              </Link>
              <Link
                to="/student-portal"
                onClick={() => setMobileOpen(false)}
                className="py-3.5 text-[15px] font-bold uppercase tracking-[0.06em] text-[var(--ac-ink)] hover:text-[var(--ac-red)] border-b border-[var(--ac-line)]"
                data-testid="mobile-nav-student-portal"
              >
                Student Portal
              </Link>
              <button
                type="button"
                onClick={() => { setMobileOpen(false); navigate("/search"); }}
                className="py-3.5 inline-flex items-center gap-2 text-[15px] font-bold uppercase tracking-[0.06em] text-[var(--ac-ink)] hover:text-[var(--ac-red)] border-b border-[var(--ac-line)] text-left"
                data-testid="mobile-nav-search"
              >
                <Search className="w-4 h-4" /> Search
              </button>
            </div>

            {/* Single primary action — Get Started. The previous Phone icon +
                "Call: …" text was removed per brief 2026-05-04 (the floating
                navy FAB already exposes call as a one-tap action). */}
            <Link
              to="/admissions#book"
              onClick={(e) => {
                e.preventDefault();
                setMobileOpen(false);
                openRequestInfo();
              }}
              className="mt-5 inline-flex items-center justify-center gap-1.5 text-[14px] font-bold uppercase tracking-[0.08em] px-5 py-3.5 rounded-md bg-[var(--ac-red)] text-white hover:brightness-110 transition"
              data-testid="mobile-book-appointment"
            >
              Get Started
            </Link>
          </div>
        </div>,
        document.body
      )}

      {/* Floating navy FAB — single-tap call action, bottom-right.
          Replaces the previous sticky white action bar per brief 2026-04-29.
          Design spec:
            • 56×56 circle, solid navy #003366 (logo-shield blue)
            • White outline phone icon, centred
            • 20 px from bottom & right edges (above the chat widget)
            • Slow 3.6 s pulsing blue ring radiating outward (heartbeat glow)
            • z-index 9999 so it floats above every other element
            • Respects prefers-reduced-motion (pulse disabled)
          2026-06-15 — Portalled to <body> so iOS Safari can't promote
          the parent <header> into a containing block (which was pushing
          the FAB to the top-right and hiding the hamburger menu). */}
      <style>{`
        @keyframes ac-fab-pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(0, 51, 102, 0.55),
                        0 0 0 0 rgba(0, 51, 102, 0.35);
          }
          70% {
            box-shadow: 0 0 0 18px rgba(0, 51, 102, 0),
                        0 0 0 36px rgba(0, 51, 102, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(0, 51, 102, 0),
                        0 0 0 0 rgba(0, 51, 102, 0);
          }
        }
        .ac-call-fab {
          animation: ac-fab-pulse 3.6s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .ac-call-fab { animation: none; }
        }
      `}</style>
      {typeof document !== "undefined" && createPortal(
        <a
          href={CONTACT.phoneHref}
          className="lg:hidden ac-call-fab fixed inline-flex items-center justify-center w-14 h-14 rounded-full text-white transition hover:brightness-110"
          style={{
            // Explicit top/left = auto so iOS Safari can't reinterpret
            // missing values when an ancestor is promoted to a CB.
            top: "auto",
            left: "auto",
            // Bottom-most floating element on mobile. Chat bubble sits
            // ABOVE this FAB via DiscoveryChatBubble.css @media rules
            // (bottom: 90px = 20 + 56 FAB + 14 gap).
            // Previous value of 96px caused both fabs to sit in the same
            // vertical band and overlap on Samsung devices (phone z-9999
            // covered chat z-9998). Also lifts with the consent-banner
            // offset (same variable the chat bubble respects) so the
            // banner never hides the call CTA.
            bottom: "calc(20px + var(--ac-banner-offset, 0px) + env(safe-area-inset-bottom, 0px))",
            right: "20px",
            zIndex: 9999,
            backgroundColor: "#003366",
          }}
          aria-label={`Call ${CONTACT.phone}`}
          data-testid="mobile-call-fab"
        >
          <Phone className="w-6 h-6" strokeWidth={1.75} />
        </a>,
        document.body
      )}
    </header>
  );
};

export default Header;
